import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const SOCIALS = [
  { Icon: Facebook,  href: 'https://www.facebook.com/LifeGate',       label: 'Facebook' },
  { Icon: Instagram, href: 'https://www.instagram.com/lifegate/',     label: 'Instagram' },
  { Icon: Linkedin,  href: 'https://www.linkedin.com/company/lifegate-spa/', label: 'LinkedIn' },
  { Icon: Youtube,   href: 'https://www.youtube.com/user/lifegate',   label: 'YouTube' },
];

const isInternal = (href) => typeof href === 'string' && href.startsWith('/');

const Footer = () => {
  const { t, lang } = useLanguage();

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollLabel = { it: 'Torna in alto', en: 'Back to top', fr: 'Retour en haut', de: 'Nach oben', es: 'Volver arriba' }[lang] || 'Back to top';

  return (
    <footer className="bg-[#1B2F1B] text-white pt-16 pb-8 px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/15">
          {/* Logo & social */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img
                src="/media/logo/bianco.svg"
                alt="LifeGate Company"
                className="h-9 w-auto"
              />
            </div>
            <p className="text-white/70 text-sm mb-6 max-w-sm">{t.footer.tagline}</p>
            <div className="flex gap-3" role="list">
              {SOCIALS.map(({ Icon, href, label }, i) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${t.footer.socialPrefix} ${label}`}
                  title={`${t.footer.socialPrefix} ${label}`}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {Object.values(t.footer.columns).map((col, idx) => (
            <div key={idx}>
              <h5 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">{col.title}</h5>
              <ul className="space-y-2.5">
                {col.items.map((item, i) => (
                  <li key={i}>
                    {isInternal(item.href) ? (
                      <Link to={item.href} className="text-white/70 hover:text-white text-sm transition">{item.label}</Link>
                    ) : (
                      <a href={item.href} target="_blank" rel="noreferrer" className="text-white/70 hover:text-white text-sm transition">{item.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-xs text-center md:text-left">{t.footer.copyright}</p>
          <div className="flex items-center gap-5 flex-wrap justify-center">
            {t.footer.legal.map((l, i) => {
              // Support both legacy string entries (fallback) and new {label,href}
              const label = typeof l === 'string' ? l : l.label;
              const href = typeof l === 'string' ? '#' : l.href;
              const external = !isInternal(href);
              return isInternal(href) ? (
                <Link key={i} to={href} className="text-white/60 hover:text-white text-xs transition">{label}</Link>
              ) : (
                <a
                  key={i}
                  href={href}
                  {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  className="text-white/60 hover:text-white text-xs transition"
                  data-testid={`footer-legal-${i}`}
                >{label}</a>
              );
            })}
            <button
              type="button"
              onClick={() => window.lgReopenCookiePreferences && window.lgReopenCookiePreferences()}
              className="text-white/60 hover:text-white text-xs transition"
              data-testid="footer-manage-cookies"
            >
              {({ it: 'Gestisci cookie', en: 'Manage cookies', fr: 'Gérer les cookies', de: 'Cookie-Einstellungen', es: 'Gestionar cookies' })[lang] || 'Manage cookies'}
            </button>
            <button
              onClick={scrollTop}
              aria-label={scrollLabel}
              title={scrollLabel}
              data-testid="footer-scroll-top"
              className="ml-2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
